import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { IoService } from '../../../services/io/io.service';
import { AppComponent } from '../../../app.component';
import { LoadingComponent } from '../../shared/loading/loading.component';
import { DialogOption } from '../../shared/ui/dialog/dialog-options';

// Full-screen editor for PurchaseType documents, opened from the Purchase Types
// configuration report (report option dialogKey: 'purchase-type-editor').
// Server counterpart: /purchaseTypeEditor (gated by PurchaseType.manage).
@Component({
  selector: 'app-purchase-type-editor',
  templateUrl: './purchase-type-editor.component.html',
  styleUrls: ['./purchase-type-editor.component.scss']
})
export class PurchaseTypeEditorComponent implements OnInit {

  purchaseTypeId: string = null;
  isNew = false;
  purchaseType: any = null;
  saving = false;

  locationTypes = ['', 'retail', 'warehouse', 'vendor', 'retail,warehouse'];
  authContexts = [
    { key: '', name: '(none)' },
    { key: 'locationId', name: 'locationId (source)' },
    { key: 'customerId', name: 'customerId (target)' },
    { key: 'general', name: 'general (unscoped)' },
  ];
  // Bulk Add import default for a mapped Quantity column (PurchaseType.importQtySemantics);
  // blank keeps the legacy rule: additive for store-to-store, a target count otherwise.
  importQtySemanticsOptions = [
    { key: '', name: 'Default (add for store-to-store, otherwise set count)' },
    { key: 'add', name: 'Add this many' },
    { key: 'target', name: 'Set count to this (difference is added)' },
  ];
  // Who may CREATE a transfer of this type — separate from who works the first
  // step; empty inherits the first step's authContext (legacy behavior).
  createAuthContexts = [
    { key: '', name: '(first step\u2019s context)' },
    { key: 'locationId', name: 'locationId (source)' },
    { key: 'customerId', name: 'customerId (target)' },
    { key: 'general', name: 'general (unscoped)' },
  ];
  stepAuthContexts = [
    { key: '', name: '(inherit from type)' },
    { key: 'locationId', name: 'locationId (source)' },
    { key: 'customerId', name: 'customerId (target)' },
    { key: 'general', name: 'general (unscoped)' },
  ];
  fieldTypes = ['', 'number', 'currency', 'string', 'dateTime'];
  fieldClasses = ['', 'short-field', 'note-field'];
  opTypes = ['copyItemField', 'setItemField', 'clearItemField', 'sortItemsBy'];
  requiredLocationKeyOptions = ['locationId', 'customerId'];

  public static showModal(purchaseTypeId: string | null = null): MatDialogRef<PurchaseTypeEditorComponent> {
    return AppComponent.dialog.open(PurchaseTypeEditorComponent, {
      // Percentages resolve against the full-screen overlay wrapper — vw/vh units
      // resolve inconsistently inside this app's overlay container.
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      panelClass: 'full-screen-dialog',
      hasBackdrop: true,
      disableClose: false,
      data: {
        purchaseTypeId,
      },
    });
  }

  constructor(
    private ioService: IoService,
    private dialogRef: MatDialogRef<PurchaseTypeEditorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.purchaseTypeId = data ? data.purchaseTypeId : null;
  }

  ngOnInit(): void {
    this.isNew = !this.purchaseTypeId;

    if (this.isNew) {
      this.purchaseType = this.normalize({});
    } else {
      this.load();
    }
  }

  load = async () => {
    LoadingComponent.showLoading();
    try {
      const purchaseType = await this.ioService.post('/purchaseTypeEditor/getPurchaseType', {
        purchaseTypeId: this.purchaseTypeId,
      });
      this.purchaseType = this.normalize(purchaseType);
    } catch (err) {
      AppComponent.showSnack(err.error && err.error.msg ? err.error.msg : 'Unable to load purchase type.', 'OK', 5000);
      this.dialogRef.close(null);
    }
    LoadingComponent.hideLoading();
  }

  // Give every editable structure a stable shape so the template can bind without
  // guards, while unknown document fields pass through untouched on save.
  private normalize(doc: any): any {
    const purchaseType = { ...doc };

    purchaseType.name = purchaseType.name || '';
    purchaseType.key = purchaseType.key || '';
    purchaseType.active = purchaseType.active !== false;
    // A side may accept several location types (e.g. adjustments at a store OR
    // the warehouse): stored as an array, edited as the comma-joined option.
    purchaseType.sourceType = Array.isArray(purchaseType.sourceType) ? purchaseType.sourceType.join(',') : (purchaseType.sourceType || '');
    purchaseType.sourceLabel = purchaseType.sourceLabel || '';
    purchaseType.targetType = Array.isArray(purchaseType.targetType) ? purchaseType.targetType.join(',') : (purchaseType.targetType || '');
    purchaseType.targetLabel = purchaseType.targetLabel || '';
    purchaseType.buildLabel = purchaseType.buildLabel || '';
    purchaseType.authContext = purchaseType.authContext || '';
    purchaseType.keyPrefix = purchaseType.keyPrefix || '';
    purchaseType.allowNegativeQty = purchaseType.allowNegativeQty !== false;

    purchaseType.inventoryFilters = purchaseType.inventoryFilters || {};
    purchaseType.inventoryFilters.requiredLocationKeys = purchaseType.inventoryFilters.requiredLocationKeys || [];

    purchaseType.transit = purchaseType.transit || {};
    purchaseType.discrepancy = purchaseType.discrepancy || {};
    purchaseType.customFields = (purchaseType.customFields || []).map(field => ({
      name: field.name || '',
      key: field.key || '',
      required: field.required === true,
    }));
    purchaseType.subtypes = purchaseType.subtypes || [];

    purchaseType.steps = (purchaseType.steps || []).map(step => this.normalizeStep(step));

    return purchaseType;
  }

  private normalizeStep(step: any): any {
    const normalized = { ...step };
    normalized.key = normalized.key || '';
    normalized.name = normalized.name || '';
    normalized.color = normalized.color || '';
    normalized.authContext = normalized.authContext || '';
    normalized.permissionAction = normalized.permissionAction || '';
    normalized.proceedPermissionAction = normalized.proceedPermissionAction || '';
    normalized.proceedLabel = normalized.proceedLabel || '';
    normalized.regressLabel = normalized.regressLabel || '';
    normalized.confirmProceed = normalized.confirmProceed || '';
    normalized.confirmRegress = normalized.confirmRegress || '';
    normalized.allowAddRemove = normalized.allowAddRemove === true;
    normalized.allowRegress = normalized.allowRegress !== false;
    normalized.requireCount = normalized.requireCount === true;
    normalized.requireReconcile = normalized.requireReconcile === true;
    normalized.inventory = {
      removeFromSource: !!(step.inventory && step.inventory.removeFromSource),
      addToTarget: !!(step.inventory && step.inventory.addToTarget),
      breakCase: !!(step.inventory && step.inventory.breakCase),
    };
    normalized.fields = (normalized.fields || []).map(field => ({
      name: field.name !== undefined ? field.name : (field.label || ''),
      key: field.key || '',
      type: field.type !== undefined ? field.type : (field.pipe || ''),
      class: field.class || '',
      editable: field.editable === true,
      displayMobile: field.displayMobile === true,
      required: field.required === true,
      default: field.default === undefined ? '' : String(field.default),
      unitOf: field.unitOf || '',
    }));
    normalized.infoFields = (normalized.infoFields || []).map(field => ({
      name: field.name || '',
      key: field.key || '',
      type: field.type || '',
    }));
    normalized.onEnter = (normalized.onEnter || []).map(op => this.normalizeOp(op));
    normalized.onRegress = (normalized.onRegress || []).map(op => this.normalizeOp(op));
    normalized.collapsed = true;
    return normalized;
  }

  private normalizeOp(op: any): any {
    return {
      op: op.op || 'copyItemField',
      from: op.from || '',
      to: op.to || '',
      key: op.key || '',
      value: op.value === undefined ? '' : String(op.value),
    };
  }

  // ----- Collection editing helpers -----

  addStep() {
    this.purchaseType.steps.push(this.normalizeStep({ allowRegress: true }));
    this.purchaseType.steps[this.purchaseType.steps.length - 1].collapsed = false;
  }

  removeStep(index: number) {
    this.purchaseType.steps.splice(index, 1);
  }

  moveStep(index: number, direction: number) {
    const target = index + direction;
    if (target < 0 || target >= this.purchaseType.steps.length) {
      return;
    }
    const steps = this.purchaseType.steps;
    [steps[index], steps[target]] = [steps[target], steps[index]];
  }

  addStepField(step) {
    step.fields.push({ name: '', key: '', type: '', class: 'short-field', editable: false, displayMobile: false, required: false, default: '', unitOf: '' });
  }

  removeStepField(step, index: number) {
    step.fields.splice(index, 1);
  }

  addInfoField(step) {
    step.infoFields.push({ name: '', key: '', type: '' });
  }

  removeInfoField(step, index: number) {
    step.infoFields.splice(index, 1);
  }

  addOp(step, listKey: string) {
    step[listKey].push({ op: 'copyItemField', from: '', to: '', key: '', value: '' });
  }

  removeOp(step, listKey: string, index: number) {
    step[listKey].splice(index, 1);
  }

  addCustomField() {
    this.purchaseType.customFields.push({ name: '', key: '', required: false });
  }

  removeCustomField(index: number) {
    this.purchaseType.customFields.splice(index, 1);
  }

  addSubtype() {
    this.purchaseType.subtypes.push({ name: '', key: '' });
  }

  removeSubtype(index: number) {
    this.purchaseType.subtypes.splice(index, 1);
  }

  toggleRequiredLocationKey(key: string) {
    const keys = this.purchaseType.inventoryFilters.requiredLocationKeys;
    const index = keys.indexOf(key);
    if (index === -1) {
      keys.push(key);
    } else {
      keys.splice(index, 1);
    }
  }

  hasRequiredLocationKey(key: string): boolean {
    return this.purchaseType.inventoryFilters.requiredLocationKeys.indexOf(key) !== -1;
  }

  // ----- Save / Delete -----

  private textOrNull(value: any) {
    return value ? value : null;
  }

  // 'true' / 'false' / numeric strings serialize to their typed values.
  private parseDefault(value: string) {
    if (value === '' || value === undefined || value === null) {
      return undefined;
    }
    if (value === 'true') {
      return true;
    }
    if (value === 'false') {
      return false;
    }
    const numeric = parseFloat(value);
    return isNaN(numeric) ? value : numeric;
  }

  private serializeOp(op: any) {
    const serialized: any = { op: op.op };
    switch (op.op) {
      case 'copyItemField':
        serialized.from = op.from;
        serialized.to = op.to;
        break;
      case 'setItemField':
        serialized.key = op.key;
        serialized.value = this.parseDefault(op.value);
        break;
      case 'clearItemField':
      case 'sortItemsBy':
        serialized.key = op.key;
        break;
    }
    return serialized;
  }

  private serializeStep(step: any) {
    const serialized: any = {
      key: step.key,
      name: step.name,
    };

    for (const textKey of ['color', 'authContext', 'permissionAction', 'proceedPermissionAction', 'proceedLabel', 'regressLabel', 'confirmProceed', 'confirmRegress']) {
      if (step[textKey]) {
        serialized[textKey] = step[textKey];
      }
    }

    if (step.allowAddRemove) {
      serialized.allowAddRemove = true;
    }
    if (step.allowRegress === false) {
      // Only the explicit prohibition is meaningful — unset means regress allowed.
      serialized.allowRegress = false;
    }
    if (step.requireCount) {
      serialized.requireCount = true;
    }
    if (step.requireReconcile) {
      serialized.requireReconcile = true;
    }

    const inventory: any = {};
    if (step.inventory.removeFromSource) {
      inventory.removeFromSource = true;
    }
    if (step.inventory.addToTarget) {
      inventory.addToTarget = true;
    }
    if (step.inventory.breakCase) {
      // Break Case completion: cases leave the target's stock, linked singles arrive.
      inventory.breakCase = true;
    }
    if (Object.keys(inventory).length > 0) {
      serialized.inventory = inventory;
    }

    const fields = step.fields.filter(field => field.key).map(field => {
      const serializedField: any = {
        name: field.name,
        key: field.key,
      };
      if (field.type) {
        serializedField.type = field.type;
      }
      if (field.class) {
        serializedField.class = field.class;
      }
      serializedField.editable = field.editable === true;
      serializedField.displayMobile = field.displayMobile === true;
      // Every line must carry a value before the step can proceed (server-enforced too).
      if (field.required === true) {
        serializedField.required = true;
      }
      const defaultValue = this.parseDefault(field.default);
      if (defaultValue !== undefined) {
        serializedField.default = defaultValue;
      }
      // Retail-unit view of a vendor-unit field (see transfer.component unitFields).
      if (field.unitOf && String(field.unitOf).trim()) {
        serializedField.unitOf = String(field.unitOf).trim();
      }
      return serializedField;
    });
    if (fields.length > 0) {
      serialized.fields = fields;
    }

    const infoFields = step.infoFields.filter(field => field.key).map(field => {
      const serializedField: any = { name: field.name, key: field.key };
      if (field.type) {
        serializedField.type = field.type;
      }
      return serializedField;
    });
    if (infoFields.length > 0) {
      serialized.infoFields = infoFields;
    }

    for (const opsKey of ['onEnter', 'onRegress']) {
      const ops = step[opsKey].filter(op => op.op).map(op => this.serializeOp(op));
      if (ops.length > 0) {
        serialized[opsKey] = ops;
      }
    }

    return serialized;
  }

  buildPayload() {
    const payload: any = {};

    for (const textKey of ['key', 'name', 'sourceType', 'sourceLabel', 'targetType', 'targetLabel', 'buildLabel', 'authContext', 'keyPrefix',
      'itemViewKey', 'itemReportKey', 'importQtySemantics']) {
      payload[textKey] = this.textOrNull(this.purchaseType[textKey]);
    }
    // Multi-type sides go back as arrays ("retail,warehouse" → ["retail", "warehouse"])
    for (const typeKey of ['sourceType', 'targetType']) {
      if (typeof payload[typeKey] === 'string' && payload[typeKey].indexOf(',') !== -1) {
        payload[typeKey] = payload[typeKey].split(',').map(part => part.trim()).filter(part => part !== '');
      }
    }

    // Never unset the identifying fields; leave them as-is when cleared
    if (!payload.key) {
      delete payload.key;
    }
    if (!payload.name) {
      delete payload.name;
    }

    payload.active = this.purchaseType.active !== false;

    for (const booleanKey of ['allowAddAll', 'defaultPendingQty', 'requirePendingCount', 'hideCostTotal', 'defaultCostFromPrice', 'unlisted', 'countAsOf', 'countAsOfLine', 'wholeVendorUnits', 'breakCase']) {
      payload[booleanKey] = this.purchaseType[booleanKey] ? true : null;
    }

    // allowNegativeQty: only the explicit false is meaningful (blocks negatives);
    // the toggle ON returns the document to the permissive default.
    payload.allowNegativeQty = this.purchaseType.allowNegativeQty === false ? false : null;
    payload.invertNegativeQty = this.purchaseType.invertNegativeQty ? true : null;

    const addUponScan = parseFloat(this.purchaseType.addUponScan);
    payload.addUponScan = isNaN(addUponScan) || addUponScan === 0 ? null : addUponScan;

    // Scanning a line already on the transfer adds this many to the step's
    // input field (addUponScan types already count re-scans and take precedence).
    const rescanAddQty = parseFloat(this.purchaseType.rescanAddQty);
    payload.rescanAddQty = isNaN(rescanAddQty) || rescanAddQty === 0 ? null : rescanAddQty;

    // Transfer code reads inventoryFilters unguarded — always keep the object present.
    payload.inventoryFilters = this.purchaseType.inventoryFilters;

    for (const plumbingKey of ['transit', 'discrepancy']) {
      const plumbing: any = {};
      if (this.purchaseType[plumbingKey].locationType) {
        plumbing.locationType = this.purchaseType[plumbingKey].locationType;
      }
      if (this.purchaseType[plumbingKey].locationId) {
        plumbing.locationId = this.purchaseType[plumbingKey].locationId;
      }
      payload[plumbingKey] = Object.keys(plumbing).length > 0 ? plumbing : null;
    }

    const customFields = this.purchaseType.customFields.filter(field => field.name && field.key)
      .map(field => ({ name: field.name, key: field.key, required: field.required === true }));
    payload.customFields = customFields.length > 0 ? customFields : null;

    const subtypes = this.purchaseType.subtypes.filter(subtype => subtype.name && subtype.key)
      .map(subtype => ({ name: subtype.name, key: subtype.key }));
    payload.subtypes = subtypes.length > 0 ? subtypes : null;

    const steps = this.purchaseType.steps.filter(step => step.key && step.name)
      .map(step => this.serializeStep(step));
    payload.steps = steps.length > 0 ? steps : null;

    return payload;
  }

  async save() {
    if (!this.purchaseType.name) {
      AppComponent.showSnack('Purchase type name is required.', 'OK', 5000);
      return;
    }

    const incompleteStep = this.purchaseType.steps.find(step => (step.key || step.name) && !(step.key && step.name));
    if (incompleteStep) {
      AppComponent.showSnack('Every step needs both a key and a name.', 'OK', 5000);
      return;
    }

    if (this.saving) {
      return;
    }

    this.saving = true;
    LoadingComponent.showLoading();

    try {
      if (this.isNew) {
        await this.ioService.post('/purchaseTypeEditor/createPurchaseType', {
          purchaseType: this.buildPayload(),
        });
        AppComponent.showSnack('Purchase type created.', 'OK', 3000);
      } else {
        await this.ioService.post('/purchaseTypeEditor/updatePurchaseType', {
          purchaseTypeId: this.purchaseTypeId,
          changes: this.buildPayload(),
        });
        AppComponent.showSnack('Purchase type saved.', 'OK', 3000);
      }
      this.dialogRef.close({ saved: true });
    } catch (err) {
      AppComponent.showSnack(err.error && err.error.msg ? err.error.msg : 'Unable to save purchase type.', 'OK', 5000);
    }

    this.saving = false;
    LoadingComponent.hideLoading();
  }

  deactivate() {
    const confirmRef = AppComponent.showMessage(
      'Deactivate Purchase Type',
      `Deactivate "${this.purchaseType.name}"? It will no longer be available for new transfers.`,
      ['Confirm', 'Cancel'],
      false
    );

    confirmRef.afterClosed().subscribe(async (result: DialogOption) => {
      if (!result || result.value !== 'Confirm') {
        return;
      }

      try {
        await this.ioService.post('/purchaseTypeEditor/deletePurchaseType', {
          purchaseTypeId: this.purchaseTypeId,
        });
        AppComponent.showSnack('Purchase type deactivated.', 'OK', 3000);
        this.dialogRef.close({ saved: true });
      } catch (err) {
        AppComponent.showSnack(err.error && err.error.msg ? err.error.msg : 'Unable to deactivate purchase type.', 'OK', 5000);
      }
    });
  }

  close() {
    this.dialogRef.close(null);
  }
}
